import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js'
import type { Submission } from '../../lib/types'
import { count, pct, mean, f1 } from '../../lib/stats'
import { C, hmColor } from '../../lib/colors'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const GRID_COLOR = 'rgba(1,87,98,0.07)'

const AXES  = ['prolactin', 'androgens', 'insulin', 'thyroid'] as const
const ALABS = ['Prolactin', 'Androgens', 'Insulin', 'Thyroid']

interface Props { data: Submission[] }

export function Signals({ data }: Props) {
  const N = data.length

  const sigCounts = AXES.map(a => count(data, d => d[`signal_${a}`] === 'signalled'))
  const sigPcts   = sigCounts.map(c => pct(c, N))

  const ageKeys   = ['under-18', '18-25', '26-35', '36-42', 'over-42']
  const ageGroups = ageKeys.map(k => data.filter(d => d.age_bracket === k))
  const sigByAge  = AXES.map((ax, i) => ({
    label: ALABS[i],
    data: ageGroups.map(g => pct(count(g, d => d[`signal_${ax}`] === 'signalled'), g.length)),
    backgroundColor: C.domain[i],
    borderRadius: 3,
  }))

  const tierBurden = [1, 2, 3, 4].map(t => {
    const g = data.filter(d => d.tier === t)
    return g.length ? parseFloat(f1(mean(g.map(d => AXES.filter(a => d[`signal_${a}`] === 'signalled').length)))) : 0
  })

  const sigCountDist = [0, 1, 2, 3, 4].map(n =>
    count(data, d => AXES.filter(a => d[`signal_${a}`] === 'signalled').length === n)
  )

  return (
    <section className="sec" id="signals">
      <div className="sec-hd">
        <div className="sec-label">03 · Hormone Signal Analysis</div>
        <h2 className="sec-title">Which hormonal axes are raised across the cohort?</h2>
        <p className="sec-sub">Signals: Prolactin ≥ 3 · Androgens ≥ 3 · Insulin ≥ 3 · Thyroid ≥ 4. A "signal" indicates a score above threshold in that axis.</p>
      </div>

      <div className="g4" style={{ marginBottom: 12 }}>
        {AXES.map((a, i) => {
          const p = sigPcts[i]
          const cls  = p >= 40 ? 's-sig' : 's-clear'
          const pCls = p >= 40 ? 'c-sig' : 'c-clear'
          const fCls = p >= 40 ? 'f-sig' : 'f-clear'
          return (
            <div key={a} className={`sig-stat ${cls}`}>
              <div className="ss-lbl">{ALABS[i]}</div>
              <div className={`ss-pct ${pCls}`}>{p}%</div>
              <div className="ss-n">n = {sigCounts[i]} of {N}</div>
              <div className="ss-bar">
                <div className={`ss-fill ${fCls}`} style={{ width: `${p}%` }} />
              </div>
            </div>
          )
        })}
      </div>

      <div className="g3">
        <div className="card">
          <div className="card-title">Signal prevalence by axis</div>
          <div className="card-sub">% of cohort with each PAInT axis signalled</div>
          <div className="cw h200">
            <Bar
              data={{ labels: ALABS, datasets: [{ data: sigPcts, backgroundColor: [C.a(C.teal,.75), C.rose, C.teal2, C.a(C.rose,.7)], borderRadius: 5 }] }}
              options={{ ...BASE, scales: { x: { grid: { display: false } }, y: { max: 100, grid: { color: GRID_COLOR } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Number of signals per person</div>
          <div className="card-sub">How many axes are raised simultaneously</div>
          <div className="cw h200">
            <Bar
              data={{ labels: ['0', '1', '2', '3', '4 signals'], datasets: [{ data: sigCountDist, backgroundColor: C.tier, borderRadius: 5 }] }}
              options={{ ...BASE, scales: { x: { grid: { display: false } }, y: { grid: { color: GRID_COLOR } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Signal burden by tier</div>
          <div className="card-sub">Mean number of axes raised per tier group</div>
          <div className="cw h200">
            <Bar
              data={{ labels: ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'], datasets: [{ data: tierBurden, backgroundColor: C.tier, borderRadius: 5 }] }}
              options={{ ...BASE, scales: { x: { grid: { display: false } }, y: { max: 4, grid: { color: GRID_COLOR } } } }}
            />
          </div>
        </div>
      </div>

      <div className="g2" style={{ marginTop: 12 }}>
        <div className="card">
          <div className="card-title">Signal co-occurrence matrix</div>
          <div className="card-sub">% of cohort where both axes are raised simultaneously (diagonal = axis prevalence)</div>
          <div className="hm-wrap" style={{ marginTop: 6 }}>
            <div className="hm-grid hm-sig-grid">
              <div className="hm-hdr" />
              {ALABS.map(l => <div key={l} className="hm-hdr">{l}</div>)}
              {AXES.map((rowA, i) => (
                <>
                  <div key={`lbl-${rowA}`} className="hm-row-lbl">{ALABS[i]}</div>
                  {AXES.map((colA, j) => {
                    const p = i === j
                      ? sigPcts[i]
                      : pct(count(data, d => d[`signal_${rowA}`] === 'signalled' && d[`signal_${colA}`] === 'signalled'), N)
                    const col = hmColor(p, i === j ? C.teal : C.rose)
                    return (
                      <div key={`${rowA}-${colA}`} className="hm-cell" style={{ background: col.bg, color: col.text }} title={`${ALABS[i]} + ${ALABS[j]}: ${p}%`}>
                        {p}%
                      </div>
                    )
                  })}
                </>
              ))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Signals by age bracket</div>
          <div className="card-sub">% of each age group with each axis raised</div>
          <div className="cw h220">
            <Bar
              data={{ labels: ['<18', '18–25', '26–35', '36–42', '42+'], datasets: sigByAge }}
              options={{
                ...BASE,
                scales: { x: { grid: { display: false } }, y: { max: 100, grid: { color: GRID_COLOR } } },
                plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 8, font: { size: 10 } } } },
              }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
